import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import Button from "components/button/Button";
import SubStep from "components/products/bde-home-page/SubStep";
import style from "components/products/getting-started.scss";
import StepCompletedText from "components/products/StepCompletedText";
import StepHeader from "components/products/StepHeader";
import { BmweOnboardingSteps, OnboardingProductType } from "domain/onboardings";
import { Onboarding } from "services/onboardings/OnboardingsService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";

import testIds from "testIds.json";

interface Props {
    stepIndex: number;
    stepCompleted: boolean;
    stepActive: boolean;
    stepClickable: boolean;
    saveOnboardingStep: (onboarding: Onboarding) => void;
    menuExpanded: boolean;
    stepInProgressIndex: number;
    saveStepInProgressIndex: (index: number) => void;
}

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

const TRANSLATION_KEYS = {
    TITLE: "Onboarding.gettingStartedFlow.initialSetupBmwe.title",
    STEPS: {
        ONE: "Onboarding.gettingStartedFlow.initialSetupBmwe.stepOne",
        TWO: "Onboarding.gettingStartedFlow.initialSetupBmwe.stepTwo",
        THREE: "Onboarding.gettingStartedFlow.initialSetupBmwe.stepThree",
        FOUR: "Onboarding.gettingStartedFlow.initialSetupBmwe.stepFour",
        FIVE: "Onboarding.gettingStartedFlow.initialSetupBmwe.stepFive",
        SIX: "Onboarding.gettingStartedFlow.initialSetupBmwe.stepSix",
    },
    COMPLETED: "Onboarding.gettingStartedFlow.initialSetupBmwe.completed",
    MARK_COMPLETE: "Onboarding.gettingStartedFlow.initialSetupBmwe.markComplete",
};

const dataType = OnboardingProductType.BMWE + "_" + BmweOnboardingSteps.STEP_INITIAL_SETUP;

const InitialSetupStep = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const [showContent, setShowContent] = React.useState<boolean>(false);

    const clickEvent = () => {
        if (props.stepClickable) {
            setShowContent(!showContent);
        }
    };

    React.useEffect(() => {
        if (props.stepInProgressIndex !== props.stepIndex) {
            setShowContent(false);
        }
    }, [props.stepInProgressIndex]);

    React.useEffect(() => {
        if (props.menuExpanded && props.stepClickable) {
            setShowContent(true);
        }
    }, [props.menuExpanded]);

    function createContent() {
        return (
            <>
                <SubStep iconNumber={1} translationKey={TRANSLATION_KEYS.STEPS.ONE} subStep={true} />
                <SubStep iconNumber={2} translationKey={TRANSLATION_KEYS.STEPS.TWO} subStep={true} />
                <SubStep iconNumber={3} translationKey={TRANSLATION_KEYS.STEPS.THREE} subStep={true} />
                <SubStep iconNumber={4} translationKey={TRANSLATION_KEYS.STEPS.FOUR} subStep={true} />
                <SubStep iconNumber={5} translationKey={TRANSLATION_KEYS.STEPS.FIVE} subStep={true} />
                <SubStep iconNumber={6} translationKey={TRANSLATION_KEYS.STEPS.SIX} subStep={true} />
                <div className={style.markComplete}>
                    {props.stepCompleted ? (
                        <div className={style.templateSelectedBlock}>
                            <StepCompletedText
                                successText={t(TRANSLATION_KEYS.COMPLETED)}
                                testId={testIds.workArea.onboarding.step.statusLabel}
                                dataType={dataType}
                            />
                        </div>
                    ) : (
                        <Button
                            variant={"PRIMARY"}
                            className={classNames(buttons.primaryButtonWithoutIcon)}
                            onClick={() => {
                                props.saveStepInProgressIndex(props.stepIndex);
                                props.saveOnboardingStep({
                                    step: BmweOnboardingSteps.STEP_INITIAL_SETUP,
                                    product: OnboardingProductType.BMWE,
                                });
                            }}
                            testId={testIds.workArea.onboarding.step.primaryButton}
                            data-type={dataType}
                            loadingAnimation={props.stepInProgressIndex === props.stepIndex}
                            disabled={props.stepInProgressIndex === props.stepIndex}
                        >
                            {props.stepInProgressIndex === props.stepIndex
                                ? t("Onboarding.saving")
                                : t(TRANSLATION_KEYS.MARK_COMPLETE)}
                        </Button>
                    )}
                </div>
            </>
        );
    }

    return (
        <StepHeader
            title={t(TRANSLATION_KEYS.TITLE)}
            success={props.stepCompleted}
            showStep={showContent}
            inactive={!props.stepClickable}
            clickEvent={clickEvent}
            stepIndex={props.stepIndex}
            showContent={showContent}
            menuExpanded={props.menuExpanded}
            content={createContent()}
            type={OnboardingProductType.BMWE}
            stepName={BmweOnboardingSteps.STEP_INITIAL_SETUP}
        />
    );
};

export default connector(InitialSetupStep);
